import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDebounce } from "use-debounce";

import type { OrganizationUserDto, Pagination } from "@superblocksteam/shared";

import { selectOnlyOrganizationId } from "store/slices/organizations";
import { callServer, HttpMethod } from "store/utils/client";
import { sendErrorUINotification } from "utils/notification";

/** Rows a picker shows per request. The server caps a page at 150. */
export const USER_SEARCH_PAGE_SIZE = 25;

const SEARCH_DEBOUNCE_MS = 300;
export const USER_SEARCH_ERROR_MESSAGE =
  "Failed to search users. Please try again.";

/** Status filter values the paged users endpoint accepts. */
export type OrgUserPageStatus = "active" | "pending" | "deactivated";

/** Invite pickers page size. */
export const INVITEE_SEARCH_PAGE_SIZE = 20;
/** Deactivated users cannot be invited. */
export const SHAREABLE_USER_STATUSES: OrgUserPageStatus[] = [
  "active",
  "pending",
];

type Options = {
  /** Raw input text; debounced before it reaches the server. */
  search: string;
  /** Fetch nothing until true, e.g. until the picker opens. */
  enabled: boolean;
  limit?: number;
  statuses?: OrgUserPageStatus[];
  roleIds?: string[];
  sort?: "name" | "email";
  /** Defaults to the current organization. */
  organizationId?: string;
};

export type OrgUserSearchResult = {
  users: OrganizationUserDto[];
  total: number;
  /** No result for the current search text yet (debouncing or in flight). */
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
};

type Loaded = {
  key: string;
  page?: Pagination<OrganizationUserDto>;
  failed?: boolean;
};

/**
 * One page of org users matching `search`, from the paged users endpoint.
 * The server ignores searches shorter than two characters, so those are
 * filtered locally within the page. Lives in core because the billing
 * modals that use it are rendered from both the app and the dashboard.
 */
export function useOrgUserSearch({
  search,
  enabled,
  limit = USER_SEARCH_PAGE_SIZE,
  statuses,
  roleIds,
  sort,
  organizationId: organizationIdOverride,
}: Options): OrgUserSearchResult {
  const organizationId = useSelector(
    (state: Parameters<typeof selectOnlyOrganizationId>[0]) =>
      organizationIdOverride ?? selectOnlyOrganizationId(state),
  );
  const trimmed = search.trim();
  const [debounced] = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);
  const params = useMemo(() => {
    const out: Record<string, string> = {
      limit: String(limit),
      offset: "0",
    };
    if (debounced.length > 1) out.search = debounced;
    if (statuses) out.status = statuses.join(",");
    if (roleIds) out.roleId = roleIds.join(",");
    if (sort) out.sort = sort;
    return out;
    // Joined so equal arrays from new renders keep one key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, debounced, statuses?.join(","), roleIds?.join(","), sort]);
  const key = `${organizationId ?? ""}?${new URLSearchParams(params).toString()}`;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reloads, setReloads] = useState(0);
  const active = enabled && !!organizationId;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    callServer<Pagination<OrganizationUserDto>>(
      {
        method: HttpMethod.Get,
        url: `v1/organizations/${organizationId}/users`,
        query: params,
      },
      { notifyOnError: false, shouldThrowHttpError: true },
    )
      .then((page) => {
        if (!cancelled) setLoaded({ key, page });
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded({ key, failed: true });
        // Pickers render an empty list on failure, which reads as "no such
        // user"; the notification says the search itself failed.
        sendErrorUINotification({ message: USER_SEARCH_ERROR_MESSAGE });
      });
    return () => {
      cancelled = true;
    };
  }, [active, organizationId, params, key, reloads]);

  const current = loaded?.key === key ? loaded : undefined;
  const users = useMemo(() => {
    const items = current?.page?.items ?? [];
    if (debounced.length !== 1) return items;
    const needle = debounced.toLowerCase();
    return items.filter(
      (user) =>
        user.name?.toLowerCase().includes(needle) ||
        user.email.toLowerCase().includes(needle),
    );
  }, [current, debounced]);

  return {
    users,
    total: current?.page?.total ?? 0,
    isPending: active && (!current || debounced !== trimmed),
    isError: !!current?.failed,
    refetch: () => setReloads((n) => n + 1),
  };
}

/** Display name for a user: name, or email when the name is blank. */
export function orgUserDisplayName(user: OrganizationUserDto): string {
  return user.name?.trim() || user.email;
}
